import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  AccreditationEligibilityCriteriaEnumType,
  AccreditationInvestorStageEnumType,
  AccreditationStatusEnumType,
} from '__generated__/graphql';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import Badge from 'primitives/Badge';
import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';
import Select from 'primitives/Select';
import TextField from 'primitives/TextField';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import FilterPanel from 'components/FilterPanel';

import debounce from 'utils/debounce';
import accreditationCriteriaToReadable from 'utils/enums/accreditation-criteria-to-readable';
import accreditationInvestorStageToReadable from 'utils/enums/accreditation-investor-stage-to-readable';
import accreditationStatusToReadable from 'utils/enums/accreditation-status-to-readable';
import useScroll from 'utils/hooks/use-scroll';

import GenerateListDialogButton from './GenerateListDialogButton';

const ACCREDITATION_QUERY = gql(`
  query getAccreditationSubmissions($cursor: ID, $limit: Int, $filters: InvestmentEntitiesFilterType) {
    accreditationSubmissions(cursor: $cursor, limit: $limit, filters: $filters) {
      nodes {
        id
        name
        user {
          id
          email
        }
        investorApplication {
          accreditationStatus
          accreditationEligibilityCriteria
        }
      }

      pageInfo {
        hasNextPage
        cursor
        totalCount
      }
    }
  }
`);

const DOWNLOAD_ACCREDITATION_CSV_MUTATION = gql(`
  mutation DownloadAccreditationCsv($type: AccreditationStatusEnumType) {
    downloadAccreditedInvestorData(type: $type)
  }
`);

const GENERATE_ACCREDITATION_REMINDER_INVESTOR_LIST_MUTATION = gql(`
  mutation GenerateAccreditationReminderInvestorList {
    generateAccreditationReminderInvestorList {
      id
      name
      user {
        id
        email
      }
      type
      status
      createdAt
      updatedAt
    }
  }
`);

const SEND_AUTOMATED_ACCREDITATION_REMINDER_EMAILS_MUTATION = gql(`
  mutation SendAutomatedAccreditationReminderEmails {
    sendAutomatedAccreditationReminderEmails
  }
`);

const Accreditation: FC = () => {
  const navigate = useNavigate();
  const { onScroll, onComponentUnmount, onDataLoad, onReset } = useScroll({
    pageIdentifier: 'accreditationPageScrollPosition',
  });
  const [csvDownloadType, setCsvDownloadType] = useState<AccreditationStatusEnumType>(
    AccreditationStatusEnumType.InitiateProcess
  );
  const [showGenerateListDialog, setShowGenerateListDialog] = useState(false);

  const { loading, error, data, refetch, fetchMore, variables } = useQuery(ACCREDITATION_QUERY, {
    variables: {
      limit: 50,
      filters: {
        text: '',
        accreditationStatus: AccreditationStatusEnumType.InitiateProcess,
      },
    },
    notifyOnNetworkStatusChange: true,
  });

  const [downloadAccreditationCsv, { loading: downloadLoading }] = useMutation(
    DOWNLOAD_ACCREDITATION_CSV_MUTATION
  );

  const [
    generateAccreditationReminderInvestorList,
    { loading: generateListLoading, error: generateListError, data: generateListData },
  ] = useMutation(GENERATE_ACCREDITATION_REMINDER_INVESTOR_LIST_MUTATION);

  const [sendAutomatedAccreditationReminderEmails, { loading: sendRemindersLoading }] = useMutation(
    SEND_AUTOMATED_ACCREDITATION_REMINDER_EMAILS_MUTATION as any,
    {
      onCompleted: () => {
        setShowGenerateListDialog(false);
      },
    }
  );

  useEffect(() => {
    if (data?.accreditationSubmissions.nodes.length) onDataLoad();
  }, [data, onDataLoad]);

  const renderContent = () => {
    if (loading && !data) return <LoadingIndicator />;
    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const filters = [
      {
        type: 'accreditationStatus',
        label: 'Accreditation Status',
        readable: (value: AccreditationStatusEnumType) => {
          return accreditationStatusToReadable(value);
        },
        options: [
          {
            value: AccreditationStatusEnumType.PendingInvestorResponse,
            label: 'Pending Investor Response',
            color: 'bg-gray-400',
          },
          {
            value: AccreditationStatusEnumType.InitiateProcess,
            label: 'Initiate Process',
            color: 'bg-amber-300',
          },
          {
            value: AccreditationStatusEnumType.PendingAlindiaVerification,
            label: 'Pending AL India Verification',
            color: 'bg-orange-400',
          },
          {
            value: AccreditationStatusEnumType.Accredited,
            label: 'Accredited',
            color: 'bg-green-400',
          },
          {
            value: AccreditationStatusEnumType.NotAccredited,
            label: 'Not Accredited',
            color: 'bg-red-500',
          },
        ],
      },
      {
        type: 'accreditationInvestorStage',
        label: 'Investor Stage',
        readable: (value: AccreditationInvestorStageEnumType) => {
          return accreditationInvestorStageToReadable(value);
        },
        options: [
          {
            value: AccreditationInvestorStageEnumType.AccreditationInitiateProcess,
            label: 'Initial Email Sent',
            color: 'bg-green-400',
          },
          {
            value: AccreditationInvestorStageEnumType.AccreditationReminder,
            label: 'Reminder Email Sent',
            color: 'bg-amber-300',
          },
        ],
      },
    ];

    return (
      <>
        <div className="p-4 w-full border-[0.5px] rounded-lg mb-4 flex flex-row justify-between">
          <div>
            <h3 className="mb-4 font-semibold">Download Accreditation Data (CSV)</h3>
            <div className="w-full">
              <Select
                options={[
                  {
                    value: AccreditationStatusEnumType.PendingInvestorResponse,
                    label: 'Pending Investor Response',
                    color: 'bg-gray-400',
                  },
                  {
                    value: AccreditationStatusEnumType.InitiateProcess,
                    label: 'Initiate Process',
                    color: 'bg-amber-300',
                  },
                  {
                    value: AccreditationStatusEnumType.PendingAlindiaVerification,
                    label: 'Pending AL India Verification',
                    color: 'bg-orange-400',
                  },
                  {
                    value: AccreditationStatusEnumType.Accredited,
                    label: 'Accredited',
                    color: 'bg-green-400',
                  },
                  {
                    value: AccreditationStatusEnumType.NotAccredited,
                    label: 'Not Accredited',
                    color: 'bg-red-500',
                  },
                ]}
                value={csvDownloadType}
                fullWidth
                onChange={e => setCsvDownloadType(e as AccreditationStatusEnumType)}
              />
            </div>
          </div>
          <Button
            onClick={async () => {
              await downloadAccreditationCsv({
                variables: {
                  type: csvDownloadType as AccreditationStatusEnumType,
                },
              }).then(({ data }) => {
                const url = data?.downloadAccreditedInvestorData;
                if (!url) return;
                window.open(url, '_blank');
              });
            }}
            className="h-min self-end"
            loading={downloadLoading}
          >
            Download
          </Button>
        </div>
        <div className="flex flex-row gap-4 items-center justify-between p-4 w-full border-[0.5px] rounded-lg mb-4">
          <p className="text-sm text-gray-500">
            Generate a list of investors who have not yet started the accreditation process. This
            will NOT trigger any emails.
          </p>
          <GenerateListDialogButton
            className="h-min self-end"
            data={generateListData?.generateAccreditationReminderInvestorList || []}
            loading={generateListLoading}
            error={generateListError}
            showDialog={showGenerateListDialog}
            onOpenDialog={() => {
              setShowGenerateListDialog(true);
              generateAccreditationReminderInvestorList();
            }}
            onCloseDialog={() => setShowGenerateListDialog(false)}
            onSendReminders={() => {
              sendAutomatedAccreditationReminderEmails();
            }}
            sendRemindersLoading={sendRemindersLoading}
          />
        </div>
        <FilterPanel
          onFilterChange={onReset}
          activeFilters={variables?.filters}
          filters={filters}
          refetch={refetch}
        />

        <div className="flex justify-between items-end mt-8">
          <TextField
            placeholder="Search for a user"
            onChange={debounce(e => {
              refetch({
                filters: {
                  ...variables?.filters,
                  text: e.target.value,
                },
              });
            }, 500)}
          />
          <span className="text-sm text-gray-500">
            {data?.accreditationSubmissions.nodes.length} of{' '}
            {data?.accreditationSubmissions.pageInfo.totalCount} members
          </span>
        </div>
        <DataTable
          data={data?.accreditationSubmissions.nodes || []}
          onClick={submission =>
            navigate(`/investment-entities/${submission.id}/investor-application`)
          }
          onUnmount={onComponentUnmount}
          onScroll={onScroll}
          columns={[
            {
              label: 'Name',
              fieldName: 'name',
            },
            {
              label: 'Email',
              fieldName: 'user.email',
            },
            {
              label: 'Accreditation Status',
              fieldName: 'investorApplication.accreditationStatus',
              type: 'ACCREDITATION_STATUS',
            },
            {
              label: 'Eligibility Criteria',
              fieldName: 'investorApplication.accreditationEligibilityCriteria',
              type: 'CUSTOM',
              customValue: (value: string[]) => {
                const returnValue: any[] = [];
                value.forEach(v => {
                  const { label, color } = accreditationCriteriaToReadable(
                    v as AccreditationEligibilityCriteriaEnumType
                  );
                  returnValue.push(<Badge label={label} color={color} />);
                });
                return (
                  <div className="flex flex-wrap gap-1">
                    {returnValue.map((v, index) => (
                      <div key={index}>{v}</div>
                    ))}
                  </div>
                );
              },
            },
          ]}
          hasNextPage={data?.accreditationSubmissions.pageInfo.hasNextPage}
          paginationLoading={loading}
          onLoadMore={() =>
            fetchMore({
              variables: {
                cursor: data?.accreditationSubmissions.pageInfo.cursor,
              },
            })
          }
        />
      </>
    );
  };

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation
        title="Accreditation"
        subTitle={`View accreditation information submitted by investors${
          data ? ` (${data.accreditationSubmissions.pageInfo?.totalCount || 0} submissions)` : ''
        }`}
      />
      <SidebarLayoutContent>{renderContent()}</SidebarLayoutContent>
    </SidebarLayout>
  );
};

export default Accreditation;
